import { type FormEvent, useState } from "react";
import { useRole } from "../../components/AppShell";
import { useToast } from "../../components/ui";
import { ApiError } from "../../lib/api";
import {
  useCompany,
  useQuoteLetterhead,
  useQuoteSettings,
  useSaveQuoteSettings,
  useUpdateCompany,
} from "../../lib/queries";
import type { QuoteSettings } from "../../lib/types";

type Draft = QuoteSettings & { legalName: string; legalId: string };

/**
 * What every new quote starts with: the legal name on its letterhead, whether catalog
 * prices include the IVA, how long it is valid and its conditions. Quotes already made
 * keep what they had.
 */
export function QuoteSettingsCard() {
  const role = useRole();
  const canEdit = role === "owner" || role === "admin";
  const settings = useQuoteSettings().data;
  const company = useCompany().data;
  const saveSettings = useSaveQuoteSettings();
  const saveCompany = useUpdateCompany();
  const toast = useToast();
  const [draft, setDraft] = useState<Partial<Draft>>({});
  if (!settings || !company) return null;
  const saved: Draft = { ...settings, legalName: company.legalName ?? "", legalId: company.legalId ?? "" };
  const d: Draft = { ...saved, ...draft };
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((prev) => ({ ...prev, [k]: v }));
  const dirty = JSON.stringify(d) !== JSON.stringify(saved);
  const pending = saveSettings.isPending || saveCompany.isPending;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await Promise.all([
        saveCompany.mutateAsync({ legalName: d.legalName, legalId: d.legalId }),
        saveSettings.mutateAsync({
          pricesIncludeTax: d.pricesIncludeTax,
          taxRate: d.taxRate,
          validDays: d.validDays,
          terms: d.terms,
        }),
      ]);
      setDraft({});
      toast("Ajustes de cotización guardados");
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "No se pudieron guardar.");
    }
  }

  return (
    <div className="card">
      <form className="card-body" onSubmit={onSubmit}>
        <div>
          <h3>Cotizaciones</h3>
          <p className="muted">
            Lo que lleva cada cotización nueva. Las que ya existen conservan el IVA y la vigencia con que se hicieron.
          </p>
        </div>
        <fieldset className="company-fields" disabled={!canEdit}>
          <div className="field">
            <label htmlFor="quote-legal-name">Razón social</label>
            <input
              className="input"
              id="quote-legal-name"
              placeholder="Ej.: Distribuidora del Valle S.A."
              maxLength={200}
              value={d.legalName}
              onChange={(e) => set("legalName", e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="quote-legal-id">Cédula jurídica</label>
            <input
              className="input mono"
              id="quote-legal-id"
              placeholder="Ej.: 3-101-123456"
              maxLength={40}
              value={d.legalId}
              onChange={(e) => set("legalId", e.target.value)}
            />
          </div>
        </fieldset>
        <fieldset className="choice" disabled={!canEdit}>
          <legend>Los precios del catálogo…</legend>
          <label className="check">
            <input
              type="radio"
              name="quote-tax"
              checked={!d.pricesIncludeTax}
              onChange={() => set("pricesIncludeTax", false)}
            />
            No incluyen IVA: se suma al final
          </label>
          <label className="check">
            <input
              type="radio"
              name="quote-tax"
              checked={d.pricesIncludeTax}
              onChange={() => set("pricesIncludeTax", true)}
            />
            Ya incluyen IVA: se muestra dentro del total
          </label>
        </fieldset>
        <fieldset className="company-fields" disabled={!canEdit}>
          <div className="field">
            <label htmlFor="quote-tax-rate">IVA (%)</label>
            <input
              className="input"
              id="quote-tax-rate"
              type="number"
              min={0}
              max={100}
              step="any"
              placeholder="Ej.: 13"
              value={d.taxRate}
              onChange={(e) => set("taxRate", Math.min(100, Math.max(0, Number(e.target.value) || 0)))}
            />
          </div>
          <div className="field">
            <label htmlFor="quote-valid-days">Vigencia (días)</label>
            <input
              className="input"
              id="quote-valid-days"
              type="number"
              min={1}
              max={365}
              placeholder="Ej.: 15"
              value={d.validDays}
              onChange={(e) => set("validDays", Math.min(365, Math.max(1, Number(e.target.value) || 1)))}
            />
          </div>
        </fieldset>
        <div className="field">
          <label htmlFor="quote-terms">Condiciones de cada cotización</label>
          <textarea
            className="textarea"
            id="quote-terms"
            rows={3}
            maxLength={2000}
            disabled={!canEdit}
            placeholder="Ej.: Pago contra entrega o por transferencia. Entrega en 3 días hábiles en el GAM; fuera del GAM, consultar."
            value={d.terms ?? ""}
            onChange={(e) => set("terms", e.target.value)}
          />
        </div>
        {canEdit && (
          <div>
            <button className="btn btn-primary" type="submit" disabled={!dirty || pending}>
              {pending ? "Guardando…" : "Guardar"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

const cm = (mm: number) => String(mm / 10).replace(".", ",");
const toMm = (text: string) => Math.round(Number(text.replace(",", ".")) * 10);

/**
 * The company's own letterhead: uploaded once, every quote's PDF is drawn on it. The
 * space at the top and bottom keeps its header and footer clear.
 */
export function LetterheadCard() {
  const role = useRole();
  const canEdit = role === "owner" || role === "admin";
  const settings = useQuoteSettings().data;
  const save = useSaveQuoteSettings();
  const letterhead = useQuoteLetterhead();
  const toast = useToast();
  const [draft, setDraft] = useState<{ top?: string; bottom?: string; printCompany?: boolean }>({});
  if (!settings) return null;
  const top = draft.top ?? cm(settings.marginTopMm);
  const bottom = draft.bottom ?? cm(settings.marginBottomMm);
  const printCompany = draft.printCompany ?? settings.printCompany;
  const valid = [top, bottom].every((v) => {
    const mm = toMm(v);
    return Number.isFinite(mm) && mm >= 10 && mm <= 120;
  });
  const dirty =
    toMm(top) !== settings.marginTopMm ||
    toMm(bottom) !== settings.marginBottomMm ||
    printCompany !== settings.printCompany;

  async function onFile(file: File | undefined) {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast("El membrete pesa más de 5 MB. Comprímelo o expórtalo de nuevo.");
    try {
      await letterhead.mutateAsync(file);
      setDraft({});
      toast("Membrete guardado");
    } catch (err) {
      const reason = err instanceof ApiError ? String(err.details?.reason ?? "") : "";
      toast(reason === "bad_type" ? "Sube el membrete en PDF, PNG o JPG." : "No se pudo subir el membrete.");
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await save.mutateAsync({ marginTopMm: toMm(top), marginBottomMm: toMm(bottom), printCompany });
      setDraft({});
      toast("Membrete ajustado");
    } catch {
      toast("No se pudo guardar.");
    }
  }

  const picker = (label: string, className: string) => (
    <label className={className}>
      {letterhead.isPending ? "Subiendo…" : label}
      <input
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
        hidden
        disabled={letterhead.isPending}
        onChange={(e) => onFile(e.target.files?.[0])}
      />
    </label>
  );

  return (
    <div className="card">
      <form className="card-body" onSubmit={onSubmit}>
        <div>
          <h3>Papel membretado</h3>
          <p className="muted">
            Si la empresa tiene una hoja con su diseño (logo, colores, pie de página), súbela y cada cotización se arma
            encima. PDF de una página, PNG o JPG en tamaño carta o A4 vertical, hasta 5 MB.
          </p>
        </div>
        {settings.letterhead ? (
          <div className="letterhead-file">
            <span className="mono" title={settings.letterhead.name}>
              {settings.letterhead.name}
            </span>
            {canEdit && (
              <span className="row">
                {picker("Cambiar", "btn btn-sm")}
                <button
                  className="btn btn-sm"
                  type="button"
                  disabled={letterhead.isPending}
                  onClick={() => letterhead.mutateAsync(null).then(() => toast("Membrete quitado"))}
                >
                  Quitar
                </button>
              </span>
            )}
          </div>
        ) : (
          canEdit && picker("Subir membrete", "btn letterhead-upload")
        )}
        {settings.letterhead && (
          <>
            <fieldset className="company-fields" disabled={!canEdit}>
              <div className="field">
                <label htmlFor="letterhead-top">Espacio libre arriba (cm)</label>
                <input
                  className="input"
                  id="letterhead-top"
                  inputMode="decimal"
                  placeholder="Ej.: 4"
                  value={top}
                  onChange={(e) => setDraft({ ...draft, top: e.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="letterhead-bottom">Espacio libre abajo (cm)</label>
                <input
                  className="input"
                  id="letterhead-bottom"
                  inputMode="decimal"
                  placeholder="Ej.: 2,5"
                  value={bottom}
                  onChange={(e) => setDraft({ ...draft, bottom: e.target.value })}
                />
              </div>
            </fieldset>
            <span className="faint">
              Lo que ocupan el encabezado y el pie del membrete, para que la cotización no los tape. Entre 1 y 12 cm.
            </span>
            <label className="check">
              <input
                type="checkbox"
                disabled={!canEdit}
                checked={printCompany}
                onChange={(e) => setDraft({ ...draft, printCompany: e.target.checked })}
              />
              Imprimir también el nombre y los datos de la empresa (si el membrete no los trae)
            </label>
          </>
        )}
        <div className="row letterhead-actions">
          <a className="btn" href="/api/quotes/settings/preview" target="_blank" rel="noopener">
            Ver ejemplo
          </a>
          {canEdit && settings.letterhead && (
            <button className="btn btn-primary" type="submit" disabled={!dirty || !valid || save.isPending}>
              {save.isPending ? "Guardando…" : "Guardar"}
            </button>
          )}
        </div>
        {dirty && <span className="faint">El ejemplo usa lo guardado: guarda los cambios para verlos.</span>}
      </form>
    </div>
  );
}
